import { useState, useEffect, useRef, forwardRef, useMemo } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "axios";
import BASE_URL from "../../../../../assets/global/url";
import DataTable from "react-data-table-component";
import { customStyles } from "../../../../../assets/table-style";
import swal from "sweetalert";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { Link, useNavigate } from "react-router-dom";
import useDecodeToken from "../../../../../hooks/customHook/useDecodeToken";
import { PaginationControls } from "../../../../../hooks/customHook/paginationHook/usePagination";
import { useDateValidation } from "../../../../../hooks/customHook/useDateValidation";
import {
  formatToTwoDecimal,
  parseNumber,
} from "../../../../../utils/numberFormatter";
import { constant_productCategory } from "../../../../../constants/productOptions";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import TypedDateInput from "../../../../../components/inputs/TypedDateInput";
import { useServerPagination } from "../../../../../hooks/customHook/paginationHook/useServerPagination";
import { AdjustmentCell, IndicatorCell } from "./CountingInput";
import { checkCutoffDate } from "../../../../../utils/cutoffDateValidation";

const NewCountingCreate = () => {
  const userLoggedID = useDecodeToken();
  const navigate = useNavigate();
  const [user, setUser] = useState("Admin");

  // Validations
  const { dateValidation } = useDateValidation();
  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // For input fields
  const warehouseRef = useRef(null);
  const [warehouseId, setWarehouseId] = useState("");
  const [warehouseList, setWarehouseList] = useState([]);
  const [transactionId, setTransactionId] = useState("");
  const [countingDate, setCountingDate] = useState();
  const [remarks, setRemarks] = useState("");

  const [selectedRows, setSelectedRows] = useState([]); // Already selected and submitted rows
  const [draftedRows, setDraftedRows] = useState([]); // Unconfirmed state, or temporary rows
  const countingProducts = useRef(new Map()); // Store products for counting, send in backend

  // For search filter
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("");
  const debounceTimer = useRef(null);

  // For fetchings
  const productListModalPagination = useServerPagination("about:blank", 10);
  const productListPagination = useServerPagination("about:blank", 10);
  const inventoryCountingEndpoint = `${BASE_URL}/inventoryCounting`;

  const [showModal, setShowModal] = useState(false); // For modal

  // Get the item list table
  const getProductList = (productIdList) => {
    productListPagination.updateHttpMethod("POST");
    productListPagination.updateApiUrl(
      `${inventoryCountingEndpoint}/products/summary`,
    );
    productListPagination.updateParams({
      warehouseId,
      productList: productIdList?.map((id) => `'${id}'`) || [],
    });
  };

  // Get the modal's table
  const getProductListModal = () => {
    productListModalPagination.updateHttpMethod("POST");
    productListModalPagination.updateApiUrl(
      `${inventoryCountingEndpoint}/products/summary/selectable`,
    );
    productListModalPagination.updateParams({
      warehouseId,
      productList: selectedRows?.map((id) => `'${id}'`) || [],
    });
  };

  // Function to generate inventory counting's transaction number
  const generateTransactionNumber = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const dateToday = `${year}${month}`;

    const generateTwoNum = Math.floor(10 + Math.random() * 90);
    const time = new Date()
      .toLocaleTimeString("en-GB", { hour12: false })
      .replace(/:/g, "");

    let newTransactionNumber = dateToday + time + generateTwoNum;

    setTransactionId("COUNT-" + newTransactionNumber);
  };

  // Handle actual count and actual price change
  const handleActualAmountChange = (newValue, actualAmount, row) => {
    const cleanedValue = newValue === "" ? "0" : newValue;

    productListPagination.setData((prevList) =>
      prevList.map((item) =>
        item.product_id === row.product_id
          ? { ...item, [actualAmount]: cleanedValue }
          : item,
      ),
    );

    countingProducts.current.set(row.product_id, {
      product_id: row.product_id,
      stock_management_id: null,
      [actualAmount]: cleanedValue,
      ...(actualAmount === "actual_count"
        ? { actual_price: row.actual_price }
        : { actual_count: row.actual_count }),
    });
  };

  // Helper: array filter by productId
  const removeId = (array, productId) => {
    return array.filter((id) => id !== productId);
  };

  // prettier-ignore
  // Handle table item removal
  const handleRemoveItem = (productId) => {
    countingProducts.current.delete(productId);

    // Update table fetching
    getProductList(removeId(selectedRows, productId));
    setSelectedRows((prev) => removeId(prev, productId));
  };

  // Handle individual checkbox change
  const handleSelectRow = (productId) => {
    // Unselect one row
    if (draftedRows.includes(productId)) {
      const unselectRow = (prev) => removeId(prev, productId);
      setDraftedRows(unselectRow);
      // Select one row
    } else {
      const selectRow = (prev) => [...prev, productId];
      setDraftedRows(selectRow);
    }
  };

  // Handle select all row on the current page
  const handleSelectAllRows = (isChecked) => {
    const data = productListModalPagination.data;
    const newSelectedRows = data.map((item) => item.product_id);

    // If checked add all row on the current page
    if (isChecked) {
      const addNewRows = (prev) => [...prev, ...newSelectedRows];
      setDraftedRows(addNewRows);
    }

    // If unchecked remove all row on the current page
    if (!isChecked) {
      const removeRows = (prev) =>
        prev.filter((id) => !newSelectedRows.includes(id));
      setDraftedRows(removeRows);
    }
  };

  // Get the list of warehouses
  const fetchWarehouseList = () => {
    axios
      .get(BASE_URL + "/inventoryCounting/getWarehouses")
      .then((res) => {
        setWarehouseList(
          res.data.map((item) => ({
            warehouse_id: item.warehouse_id,
            warehouse_name: item.name,
          })),
        );
      })
      .catch((error) => console.error(error));
  };

  // Handle search change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      productListModalPagination.updateHttpMethod("POST");
      productListModalPagination.updateApiUrl(
        `${inventoryCountingEndpoint}/products/summary/selectable/search`,
      );
      productListModalPagination.updateParams({
        warehouseId,
        productList: selectedRows?.map((id) => `'${id}'`) || [],
        searchText: value,
        productCategory: category,
      });
    }, 500);
  };

  // Handle clear filter
  const handleClearFilters = () => {
    setSearchTerm("");
    setCategory("");
  };

  // Handle warehouse change
  const handleWarehouseChange = (e) => {
    setWarehouseId(e.target.value);
    setSelectedRows([]);
    getProductList([]);
  };

  // Handle change category
  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    setSelectedRows([]);
  };

  // Handle show modal
  const handleShow = () => setShowModal(true);

  // Handle close modal
  const handleClose = () => {
    setShowModal(false);
    setCategory("");
    setSearchTerm("");
    setDraftedRows([]);
  };

  // Handle modal submission
  const handleSubmitModal = () => {
    getProductList([...new Set([...selectedRows, ...draftedRows])]); // Update Item List table fetching
    setSelectedRows((prev) => [...prev, ...draftedRows]);
    setShowModal(false);
    setSearchTerm("");
    setDraftedRows([]); // Clear drafted rows
  };

  // Handle add new inventory counting transaction
  // Handle add new inventory counting transaction
  const add = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    const countingItems = countingProducts.current;

    // Remove the comma and convert to number
    for (const [key, value] of countingItems.entries()) {
      const product = countingItems.get(key);
      countingItems.set(key, {
        ...product,
        actual_count: parseNumber(value.actual_count),
        actual_price: parseNumber(value.actual_price),
      });
    }

    if (countingItems.size <= 0) {
      return swal({
        icon: "error",
        title: "No items added",
        text: "Please add at least one item to count.",
      });
    }

    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill the red text fields",
      });
      setValidated(true);
    } else {
      setValidated(true);

      // 1. Set to true IMMEDIATELY so the button says "Saving..." while the modal is open
      setIsSubmitting(true);

      swal({
        title: "Are you sure?",
        text: "Do you want to proceed and save this inventory counting?",
        icon: "warning",
        buttons: ["Cancel", "Yes, Proceed"],
        dangerMode: true,
      }).then((confirmed) => {
        if (confirmed) {
          // Proceed with the API call
          axios
            .post(`${BASE_URL}/inventoryCounting/saveInventoryCounting`, {
              countingDate: countingDate,
              remarks: remarks,
              user: user,
              transactionId: transactionId,
              itemList: Object.fromEntries(countingItems),
              selectedRows,
              userLoggedID,
              warehouseId,
            })
            .then((res) => {
              if (res.status === 200) {
                setValidated(false);
                swal({
                  title: "Success",
                  text: "Inventory Counting saved successfully!",
                  icon: "success",
                  button: "OK",
                }).then(() => {
                  navigate("/inventory/inventory-counting");
                });
              } else {
                ErrorInserted();
              }
            })
            .catch((error) => {
              if (error.response && error.response.status === 409) {
                swal({
                  title: "Oopps!",
                  text: "Action is prohibited because the date provided for the Counting Time has already passed the posted cutoff.",
                  icon: "error",
                  button: "OK",
                });
              }
            })
            .finally(() => {
              setIsSubmitting(false); // Stop loading when the API finishes
            });
        } else {
          // 2. If the user clicks "Cancel", reset the button back to "Save"
          setIsSubmitting(false);
        }
      });
    }
  };

  const ErrorInserted = () => {
    swal({
      title: "Something went wrong",
      text: "Please Contact our Support",
      icon: "error",
      button: "OK",
    });
  };

  // Handle go back
  const handleCancel = () => {
    navigate("/inventory/inventory-counting");
  };

  // Item list's column (Outside table)
  const columns = [
    {
      name: "Product Name",
      selector: (row) => row.product_name,
    },
    {
      name: "System Quantity",
      selector: (row) => formatToTwoDecimal(row.stock_quantity),
    },
    {
      name: "Actual Count",
      cell: (row) => (
        <IndicatorCell
          row={row}
          fieldName="actual_count"
          baseFieldName="stock_quantity"
          onActualAmountChange={handleActualAmountChange}
        />
      ),
    },
    {
      name: "Average Price",
      selector: (row) => formatToTwoDecimal(row.average_price),
    },
    {
      name: "Actual Price",
      cell: (row) => (
        <IndicatorCell
          row={row}
          fieldName="actual_price"
          baseFieldName="average_price"
          onActualAmountChange={handleActualAmountChange}
        />
      ),
    },
    {
      name: "Adjustment",
      cell: (row) => (
        <AdjustmentCell
          row={row}
          onActualAmountChange={handleActualAmountChange}
        />
      ),
    },
    {
      name: "Action",
      cell: (row) => (
        <button
          type="button"
          className="btn btn-danger btn-sm"
          onClick={() => handleRemoveItem(row.product_id)}
        >
          Remove
        </button>
      ),
    },
  ];

  // Product columns for modal's table
  const productColumns = [
    {
      name: (
        <div className="custom-checkbox">
          <input
            type="checkbox"
            className="form-check-input"
            checked={productListModalPagination.data.every((item) =>
              draftedRows.includes(item.product_id),
            )}
            onChange={(e) => handleSelectAllRows(e.target.checked)}
          />
        </div>
      ),
      cell: (row) => {
        const productId = row.product_id;
        return (
          <div className="custom-checkbox">
            <input
              type="checkbox"
              className="form-check-input border border-secondary"
              checked={draftedRows.includes(productId)}
              onChange={() => handleSelectRow(productId)}
            />
          </div>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    { name: "Product Name", selector: (row) => row.product_name },
    { name: "Product Category", selector: (row) => row.product_category },
    {
      name: "Average Price",
      selector: (row) => formatToTwoDecimal(row.average_price),
    },
    {
      name: "Quantity",
      selector: (row) => formatToTwoDecimal(row.stock_quantity),
    },
  ];

  // Custom input for DatePicker: typed as MM/dd/yyyy and shown as Oct/10/2026
  // (TypedDateInput), or picked from the calendar. Wrapped in useMemo so the
  // input isn't remounted on every render. Only a complete date reaches
  // handleDateChange, so the cutoff check doesn't run on a half-typed date.
  const CustomInput = useMemo(() => {
    const Input = forwardRef(
      (
        {
          value,
          onClick,
          onChange,
          onBlur,
          onFocus,
          onKeyDown,
          disabled,
          generateYears,
        },
        ref,
      ) => {
        return (
          <TypedDateInput
            className="form-control p-3 w-100"
            onClick={(e) => {
              if (onClick) onClick(e);

              let selectedYear = new Date().getFullYear();
              if (value) {
                const parsed = new Date(value);
                // Extract year safely if value is a valid date
                if (!isNaN(parsed.getTime())) {
                  selectedYear = parsed.getFullYear();
                }
              }

              // Reset/Initialize Year List based on selected date
              if (generateYears && typeof generateYears === "function") {
                generateYears(selectedYear);
              }
            }}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            value={value}
            ref={ref}
            disabled={disabled}
            required
          />
        );
      },
    );

    Input.displayName = "CustomInput";
    return Input;
  }, []);

  useEffect(() => {
    generateTransactionNumber();
    fetchWarehouseList();
  }, []);

  useEffect(() => {
    if (
      countingProducts.current.size > 0 &&
      productListPagination.data?.length > 0
    ) {
      productListPagination.setData((prev) => {
        let changed = false;

        const next = prev.map((item) => {
          const product = countingProducts.current.get(item.product_id);

          if (product) {
            const newCount = product.actual_count ?? item.stock_quantity ?? "0";
            const newPrice = product.actual_price ?? item.average_price ?? "0";

            if (
              item.actual_count !== newCount ||
              item.actual_price !== newPrice
            ) {
              changed = true;
              return {
                ...item,
                actual_count: newCount,
                actual_price: newPrice,
              };
            }
          }
          return item;
        });

        return changed ? next : prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productListPagination.data]);

  return (
    <div className="h-100 w-100 bg-white">
      <div className="w-100 p-2 d-flex flex-row justify-content-between">
        <div className="d-flex flex-column title-custom">
          <span className="fs-3">
            <Link to="/inventory/inventory-counting" className="text-dark me-2">
              <i class="fa-solid fa-arrow-left"></i>
            </Link>
            NEW INVENTORY COUNTING
          </span>
        </div>
      </div>

      <Form noValidate validated={validated} onSubmit={add}>
        <div className="container-fluid mt-3">
          <div className="row mx-auto">
            <div className="col-sm mb-3">
              <span>
                Counting ID <span className="text-danger">*</span>
              </span>
              <input
                type="text"
                className="form-control custom-form-height border-0 custom-input-form-readOnly"
                value={transactionId}
                readOnly
              />
            </div>

            <div className="col-sm">
              <span>
                Warehouse <span className="text-danger">*</span>
              </span>
              <Form.Select
                id="warehouseFilter"
                className="form-control custom-form-height"
                value={warehouseId}
                onChange={handleWarehouseChange}
                ref={warehouseRef}
                required
              >
                <option value="" disabled>
                  Select Warehouses
                </option>
                {warehouseList.map((wh, index) => (
                  <option key={index} value={wh.warehouse_id}>
                    {wh.warehouse_name}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
          <div className="row mx-auto">
            <div className="col-sm mb-3">
              <span>
                Counting Date <span className="text-danger">*</span>
              </span>
              <CustomDatePicker
                label={"Counting Date"}
                selected={countingDate ? new Date(countingDate) : ""}
                handleDateChange={(date) => {
                  setCountingDate(date);
                  // Replaced by checkCutoffDate (utils/cutoffDateValidation)
                  // dateValidation(date, setCountingDate, "Counting Date");
                  // CustomInput (TypedDateInput) only sends complete dates
                  checkCutoffDate(date, "Counting Date");
                }}
                setter={setCountingDate}
                CustomInput={CustomInput}
                isRequired={true}
                validated={validated}
                // Replaced by checkCutoffDate (utils/cutoffDateValidation)
                // dateValidation={dateValidation}
                dateValidation={(date) => {
                  checkCutoffDate(date, "Counting Date");
                }}
                iconTopOffset={"1.3rem"}
                maxDate={new Date()}
              />
            </div>
            <div className="col-sm"></div>
          </div>
          <div className="row mx-auto">
            <div className="col-sm mb-3">
              <span>Remarks</span>
              <textarea
                rows="5"
                className="form-control"
                onChange={(e) => setRemarks(e.target.value)}
              ></textarea>
            </div>
            <div className="col-sm"></div>
          </div>
        </div>
        <div className="container-fluid">
          <div className="w-100 d-flex align-items-center mt-3 p-2">
            <h5>Item List</h5>
            <hr className="flex-grow-1 mx-3" />
          </div>
          <div className="w-100 mt-3 container-fluid">
            <DataTable
              columns={columns}
              data={productListPagination.data}
              customStyles={customStyles}
              className="dataTable"
            />
            <PaginationControls {...productListPagination} />
          </div>
          <div className="w-100 d-flex justify-content-end pe-3 mt-5">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                if (warehouseId === "") {
                  swal({
                    icon: "warning",
                    title: "Warning",
                    text: "Please fill the warehouse field first.",
                  }).then(() => {
                    warehouseRef.current.focus();
                    warehouseRef.current.showPicker();
                  });
                  return;
                }
                getProductListModal();
                handleShow();
              }}
            >
              New Item
            </button>
          </div>
        </div>

        <div className="container-fluid mt-5">
          <div className="row">
            <div className="col-sm mb-2"></div>
            <div className="col-sm"></div>
            <div className="col-sm"></div>
            <div className="col-sm pe-4">
              <div className="row">
                <div className="col-sm mb-2">
                  <button
                    type="button"
                    variant="outline-secondary"
                    onClick={handleCancel}
                    className="btn btn-secondary w-100"
                  >
                    Cancel
                  </button>
                </div>
                <div className="col-sm">
                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Form>

      {/* Add Modal */}
      <Modal
        show={showModal}
        onHide={handleClose}
        size="xl"
        backdrop="static"
        className="custom-modal-width"
      >
        <Modal.Header className="border-0" closeButton>
          <Modal.Title>INVENTORY | PRODUCT LIST</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="container-fluid">
            <div className="row">
              <div className="col-sm mb-2">
                <span>Product Category</span>
                <select
                  id="categoryFilter"
                  className="form-select"
                  value={category}
                  onChange={handleCategoryChange}
                >
                  <option value="">All Categories</option>
                  {constant_productCategory.map((product, index) => (
                    <option key={index} value={product}>
                      {product}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm d-flex flex-row align-items-end mb-2 filter-btn-container w-100">
                <button
                  className="btn btn-secondary"
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </button>
              </div>
              <div className="col-sm"></div>
            </div>
            <div className="w-100 mt-2 mb-2">
              <div>
                <input
                  id="searchBar"
                  type="text"
                  className="form-control"
                  placeholder="Search by Product Name"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <button className="dropdown-item">Transaction No.</button>
                  </li>
                  <li>
                    <button className="dropdown-item">
                      Receiving Warehouse
                    </button>
                  </li>
                  <li>
                    <button className="dropdown-item">Date Created</button>
                  </li>
                  <li>
                    <button className="dropdown-item">Vendor</button>
                  </li>
                  <li>
                    <button className="dropdown-item">Due Date</button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="w-100 mt-4 container-fluid">
            <DataTable
              columns={productColumns}
              data={productListModalPagination.data}
              customStyles={customStyles}
              onRowClicked={(row) => handleSelectRow(row.product_id)}
              pointerOnHover
            />
            <PaginationControls {...productListModalPagination} />
          </div>
          <Modal.Footer className="border-0 p-0 mt-5">
            <Button variant="secondary" type="button" onClick={handleClose}>
              Close
            </Button>
            <Button variant="primary" type="button" onClick={handleSubmitModal}>
              Submit
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default NewCountingCreate;
